import React, { useState, useEffect, useContext, useMemo } from "react";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Checkbox from "@amzn/meridian/checkbox";
import RadioButton from "@amzn/meridian/radio-button";
import { Line } from "react-chartjs-2";
import { FormattedMessage } from "react-intl";
import { updateMetricSelectedStat } from "../common/thunks/DashboardConfigurationDispatcher";
import { useDispatch } from "react-redux";
import { fetchMetrics } from "../common/thunks/MetricDataDispatcher";
import { buildMetricFilters } from "../common/Dashboard/DashboardUtilities";
import { getStationCode } from "../../network/NetworkUtil";
import { CycleFilterContext } from "../../overview/context/CycleFilterContext";
import {
  FIFTEEN_MIN,
  THIRTY_MIN,
  CUMULATIVE,
  ANNOTATION_LINE_DATA_POINTS,
  STAT_SUM,
  STAT_CUMULATIVE_SUM,
} from "../constants/Constants";
import { chainWalk } from "../../util/Util";

// Helper function to get available time options from widget configuration

const getAvailableTimeOptions = (widgetDefinition) => {
  const hasMetrics = chainWalk(
    () => widgetDefinition.metrics.length > 0,
    false,
  );
  if (!hasMetrics) {
    return [];
  }

  // Get all available periods and stats from all metrics
  const allPeriods = new Set();
  const allStats = new Set();

  widgetDefinition.metrics.forEach((metric) => {
    if (metric.period && Array.isArray(metric.period)) {
      metric.period.forEach((p) => allPeriods.add(p));
    }
    if (metric.stat && Array.isArray(metric.stat)) {
      metric.stat.forEach((s) => allStats.add(s));
    }
  });

  const availableOptions = [];

  // Add period-based options
  if (allPeriods.has(15)) {
    availableOptions.push({
      key: "15min",
      value: "15min",
      stat: STAT_SUM,
      messageId: "mySCC.chartWidget.fifteenMin",
      defaultMessage: FIFTEEN_MIN,
    });
  }

  if (allPeriods.has(30)) {
    availableOptions.push({
      key: "30min",
      value: "30min",
      stat: STAT_SUM,
      messageId: "mySCC.chartWidget.thirtyMin",
      defaultMessage: THIRTY_MIN,
    });
  }

  // Add cumulative option if CUMULATIVE_SUM is available
  if (allStats.has(STAT_CUMULATIVE_SUM)) {
    availableOptions.push({
      key: "cumulative",
      value: "cumulative",
      stat: STAT_CUMULATIVE_SUM,
      messageId: "mySCC.chartWidget.cumulative",
      defaultMessage: CUMULATIVE,
    });
  }

  return availableOptions;
};

export default function ChartWidget({ widgetSpec, metricData }) {
  const dispatch = useDispatch();
  const widgetDefinition = chainWalk(() => widgetSpec.widgetDefinition, null);
  // Use metricData for actual chart values
  const metrics = metricData || [];

  // Normalize chartData to always be an array
  const chartDataArray = Array.isArray(metrics) ? metrics : [metrics];

  // TODO: Remove this implementation. very hacky right now
  // Context and filter building
  const { cycles } = useContext(CycleFilterContext);
  const [filters, setFilters] = useState(null);

  // Build filters for metrics
  useEffect(() => {
    setFilters(buildMetricFilters(getStationCode(), cycles.selectedCycles));
  }, [cycles]);

  const availableTimeOptions = useMemo(
    () => getAvailableTimeOptions(widgetDefinition),
    [widgetDefinition],
  );

  // State for selected metrics - initialize with all metrics selected by default
  const [selectedMetricIds, setSelectedMetricIds] = useState(
    chartDataArray.length > 0
      ? chartDataArray.map((metric) => metric.metric.metricName)
      : [],
  );

  // State for selected stat - use first metric's selectedStat or default based on available options
  const [selectedStat, setSelectedStat] = useState(() => {
    const firstMetric = chainWalk(() => widgetDefinition.metrics[0], null);
    if (firstMetric) {
      return (
        firstMetric.selectedStat ||
        (availableTimeOptions.length > 0
          ? availableTimeOptions[0].stat
          : STAT_SUM)
      );
    }
    return availableTimeOptions.length > 0
      ? availableTimeOptions[0].stat
      : STAT_SUM;
  });

  const handleStatChange = (newTimeOption) => {
    const newStat =
      newTimeOption === "cumulative" ? STAT_CUMULATIVE_SUM : STAT_SUM;

    // Update selected stat if it changed
    if (selectedStat !== newStat) {
      setSelectedStat(newStat);

      // Update selectedStat for all metrics in this widget
      if (widgetDefinition && widgetDefinition.metrics) {
        widgetDefinition.metrics.forEach((metric) => {
          dispatch(
            updateMetricSelectedStat(widgetSpec.widgetId, metric.id, newStat),
          );
        });

        // Fetch new metrics data for all metrics in this widget
        const metricSpecs = JSON.parse(
          JSON.stringify(widgetDefinition.metrics),
        );
        metricSpecs.forEach((metric) => {
          metric.selectedStat = newStat;
        });
        dispatch(fetchMetrics(metricSpecs, filters));
      }
    }

    setTimeOption(newTimeOption);
  };

  // State for selected time option - use first available option as default
  const [timeOption, setTimeOption] = useState(() => {
    if (availableTimeOptions.length === 0) {
      return "15min"; // fallback default
    }

    // If we have a selected stat from metrics, try to match it
    const savedStat = chainWalk(
      () => widgetDefinition.metrics[0].selectedStat,
      null,
    );
    if (savedStat) {
      const matchingOption = availableTimeOptions.find(
        (option) => option.stat === savedStat,
      );
      if (matchingOption) {
        return matchingOption.value;
      }
    }

    // Default to first available option
    return availableTimeOptions[0].value;
  });

  const handleMetricToggle = (metricId, isChecked) => {
    setSelectedMetricIds((prev) =>
      isChecked ? [...prev, metricId] : prev.filter((id) => id !== metricId),
    );
  };

  // Filter chart data based on selected metric IDs
  const filteredChartData = chartDataArray.filter((metric, index) => {
    return selectedMetricIds.includes(metric.metric.metricName);
  });

  // Helper function to get data points based on selected time option
  const getDataPointsForTimeOption = (metric, timeOption) => {
    if (
      !metric.metricValues ||
      !metric.metricValues[0] ||
      !metric.metricValues[0].dataPoints
    ) {
      return [];
    }

    const dataPoints = metric.metricValues[0].dataPoints;

    // Map time option to data points key
    if (timeOption === "cumulative") {
      // For cumulative, we might need to use a different key or aggregate data
      // For now, let's use the first available data points
      return dataPoints["15"] || dataPoints["30"] || dataPoints.default || [];
    } else if (timeOption === "15min") {
      return dataPoints["15"] || dataPoints.default || [];
    } else if (timeOption === "30min") {
      return dataPoints["30"] || dataPoints.default || [];
    }

    return dataPoints.default || [];
  };

  // Show legend only when there are multiple datasets
  const showLegend = filteredChartData.length > 1;

  // Calculate chart bounds from selected metrics
  const calculateChartBounds = () => {
    if (
      !widgetDefinition ||
      !widgetDefinition.metrics ||
      !selectedMetricIds.length
    ) {
      return null;
    }

    const selectedMetrics = widgetDefinition.metrics.filter((metric) =>
      selectedMetricIds.includes(metric.id),
    );

    const boundsArray = selectedMetrics
      .map((metric) => metric.bounds && metric.bounds.vertical)
      .filter(
        (bounds) =>
          bounds && (bounds.min !== undefined || bounds.max !== undefined),
      );

    if (boundsArray.length === 0) {
      return null;
    }

    // Use union approach: min of all mins, max of all maxs
    const minValues = boundsArray
      .map((bounds) => bounds.min)
      .filter((min) => min !== undefined);
    const maxValues = boundsArray
      .map((bounds) => bounds.max)
      .filter((max) => max !== undefined);

    const result = {};
    if (minValues.length > 0) {
      result.min = Math.min(...minValues);
    }
    if (maxValues.length > 0) {
      result.max = Math.max(...maxValues);
    }

    // Validate bounds
    if (
      result.min !== undefined &&
      result.max !== undefined &&
      result.min >= result.max
    ) {
      console.warn("Invalid bounds configuration: min >= max", result);
      return null;
    }

    return Object.keys(result).length > 0 ? result : null;
  };

  const chartBounds = calculateChartBounds();

  // Recalculate bounds when selected metrics change
  useEffect(() => {
    // This effect ensures bounds are recalculated when metric selection changes
    // The chartBounds calculation will automatically run on next render
  }, [selectedMetricIds]);

  return (
    <Box
      height="100%"
      overflowX="hidden"
      overflowY="hidden"
      className="flex-column"
    >
      <Box spacingInset="200 0 300 0">
        <Row
          alignmentHorizontal={chartDataArray.length > 1 ? "justify" : "end"}
          spacing="300"
        >
          {/* Metric selection checkboxes - only show if multiple metrics */}
          {chartDataArray.length > 1 && (
            <Box width="fill">
              <Row spacing="400">
                {chartDataArray.map((metric, index) => {
                  const metricId = metric.metric.metricName;
                  return (
                    <Checkbox
                      key={metricId}
                      value={metricId}
                      checked={selectedMetricIds.includes(metricId)}
                      onChange={(e) => handleMetricToggle(metricId, e)}
                    >
                      {metric.displayName}
                    </Checkbox>
                  );
                })}
              </Row>
            </Box>
          )}

          {/* Dynamic radio buttons based on widget configuration */}
          {availableTimeOptions.length > 0 && (
            <Box width="fit">
              <Row spacing="400">
                {availableTimeOptions.map((option) => (
                  <RadioButton
                    key={option.key}
                    name="timeOption"
                    value={option.value}
                    checked={timeOption === option.value}
                    onChange={() => handleStatChange(option.value)}
                  >
                    <FormattedMessage
                      id={option.messageId}
                      defaultMessage={option.defaultMessage}
                    />
                  </RadioButton>
                ))}
              </Row>
            </Box>
          )}
        </Row>
      </Box>

      {/* Chart section */}
      <Box className="flex-grow">
        <Line
          data={{
            labels:
              filteredChartData.length > 0
                ? getDataPointsForTimeOption(
                    filteredChartData[0],
                    timeOption,
                  ).map((point) =>
                    new Date(point.timestamp).toLocaleTimeString("en-US", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  )
                : [],
            datasets: [
              // Regular line datasets
              ...filteredChartData.map((metric, index) => {
                // Predefined colors for regular lines: light blue, light green, orange
                const colors = ["#98df8a", "#add8e6", "#ff7f0e"];
                const colorIndex = index % colors.length;

                const dataPoints = getDataPointsForTimeOption(
                  metric,
                  timeOption,
                );

                return {
                  data: dataPoints.map((point) => point.value),
                  fill: false,
                  label: metric.displayName,
                  borderColor: colors[colorIndex],
                };
              }),
              // Dotted line datasets for metrics with annotation
              ...filteredChartData
                .filter((metric) => {
                  // Find the corresponding widget metric config
                  const widgetMetric = widgetDefinition.metrics.find(
                    (wm) => wm.id === metric.metric.metricName,
                  );
                  return (
                    widgetMetric &&
                    widgetMetric.annotation &&
                    widgetMetric.annotation.horizontal &&
                    widgetMetric.annotation.horizontal.length > 0
                  );
                })
                .flatMap((metric) => {
                  // Find the corresponding widget metric config
                  const widgetMetric = widgetDefinition.metrics.find(
                    (wm) => wm.id === metric.metric.metricName,
                  );

                  // Create a dataset for each horizontal annotation line
                  return widgetMetric.annotation.horizontal.flatMap(
                    (horizontalLines) => {
                      return horizontalLines.map((line) => {
                        // Determine line style based on type
                        const lineType = line.type || "line/solid";
                        const isDotted = lineType.includes("dotted");

                        return {
                          data: new Array(ANNOTATION_LINE_DATA_POINTS).fill(
                            line.value,
                          ),
                          fill: false,
                          label: `${metric.displayName} ${isDotted ? "dotted" : "line"} ${line.value}`,
                          borderColor: line.color || "#ff9999",
                          borderDash: isDotted ? [6, 5] : [], // Use dotted line only if type includes "dotted"
                          borderWidth: 1,
                          pointRadius: 0, // Hide points on dotted line
                        };
                      });
                    },
                  );
                }),
            ],
          }}
          options={{
            maintainAspectRatio: false,
            legend: {
              display: showLegend,
              position: "bottom",
              labels: {
                boxWidth: 12,
                padding: 15,
                filter: function (legendItem, chartData) {
                  // Hide legend entries for dotted line datasets (those with "dotted" in the label)
                  return !legendItem.text.includes("dotted");
                },
              },
            },
            scales: {
              xAxes: [{ gridLines: { drawOnChartArea: false } }],
              yAxes: [
                {
                  gridLines: { drawOnChartArea: false },
                  ticks: {
                    autoSkip: true,
                    maxTicksLimit: 5,
                    ...(chartBounds || {}), // Apply bounds if they exist
                  },
                },
              ],
            },
          }}
        />
      </Box>
    </Box>
  );
}



// WEBPACK FOOTER //
// ./src/components/mySCC/components/ChartWidget.js